import React, { useCallback, useMemo, useState } from 'react';
import type { LayoutChangeEvent, TextProps } from 'react-native';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { BrandIcon } from '../BrandIcon';
import { BodyText } from '../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const ProgressBar = (props: ProgressBarProps) => {
  const { nodes, maxFontSizeMultiplier, iconSrc, iconAlt } = props;

  const { nodeWidth, measureNodes } = useProgressBarMeasurement(nodes.length);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const renderProgressBarNode = useCallback(
    (node: ProgressBarNodeType) => (
      <ProgressBarNode
        key={node.id}
        node={node}
        width={nodeWidth}
        maxFontSizeMultiplier={maxFontSizeMultiplier}
        iconSrc={iconSrc}
        iconAlt={iconAlt}
      />
    ),
    [iconAlt, iconSrc, maxFontSizeMultiplier, nodeWidth],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View testID="sg-progress-bar" style={styles.progressBar} onLayout={measureNodes}>
      {nodes.map(renderProgressBarNode)}
    </View>
  );
};

const ProgressBarNode = (props: ProgressBarNodeProps) => {
  const { node, width, maxFontSizeMultiplier, iconSrc, iconAlt } = props;

  const { fontScale } = useWindowDimensions();

  const { id, label, completed, variation = 'hot-spots' } = node;

  // ─── State ────────────────────────────────────────────────────────────────

  const [bottom, setTextMarginBottom] = useState(-24);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const dynamicStyle = useStyle(() => ({ width }));
  const lineStyle = completed ? styles.completedLine : styles.pendingLine;

  const circleStyle = completed ? styles.completedCircle : styles.pendingCircle;
  const completedIconSrc = completed ? iconSrc : undefined;
  const shouldShowIcon = completedIconSrc !== undefined;

  const labelBottom = iconSrc ? bottom - BRAND_ICON_LABEL_OFFSET : bottom;
  const labelStyle = useStyle(
    () => ({ bottom: labelBottom * fontScale }),
    [fontScale, labelBottom],
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const isMultipleLines = event.nativeEvent.layout.height >= LINE_HEIGHT * 2;

    setTextMarginBottom(-(isMultipleLines ? theme.spacing['10'] : theme.spacing['6']));
  }, []);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={[dynamicStyle, styles.node]} testID={`sg-progress-bar-node-${id}`}>
      <View style={[styles.absoluteLine, lineStyle]} />

      {variation === 'hot-spots' && shouldShowIcon ? (
        <BrandIcon
          alt={iconAlt}
          src={completedIconSrc}
          size={NODE_ICON_SIZE}
          style={styles.absoluteCircle}
        />
      ) : null}

      {variation === 'hot-spots' && !shouldShowIcon ? (
        <View style={[styles.absoluteCircle, circleStyle]} />
      ) : null}

      <BodyText
        onLayout={handleLayout}
        style={[styles.label, labelStyle]}
        size={5}
        maxFontSizeMultiplier={maxFontSizeMultiplier}
      >
        {label}
      </BodyText>
    </View>
  );
};

const useProgressBarMeasurement = (nodeCount: number) => {
  // ─── State ────────────────────────────────────────────────────────────────

  const [progressBarWidth, setProgressBarWidth] = useState(640);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const nodeWidth = useMemo(() => progressBarWidth / nodeCount, [nodeCount, progressBarWidth]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const measureNodes = useCallback((event: LayoutChangeEvent) => {
    setProgressBarWidth(event.nativeEvent.layout.height);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────

  return { nodeWidth, measureNodes };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LINE_HEIGHT = 16;
const NODE_ICON_SIZE = 28;
const BRAND_ICON_LABEL_OFFSET = theme.spacing['2'];

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  progressBar: {
    flex: 1,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  node: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  absoluteLine: {
    position: 'absolute',
    left: 0,
    right: 0,
  },
  absoluteCircle: {
    position: 'absolute',
  },
  completedLine: {
    backgroundColor: theme.colors.GREEN_2,
    height: 1,
  },
  pendingLine: {
    height: 1,
    backgroundColor: theme.colors.NEUTRAL_3,
  },
  completedCircle: {
    backgroundColor: theme.colors.GREEN_2,
    borderRadius: 12,
    width: 12,
    height: 12,
  },
  pendingCircle: {
    backgroundColor: theme.colors.NEUTRAL_7,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
    borderRadius: 12,
    width: 12,
    height: 12,
  },
  label: {
    position: 'absolute',
    color: theme.colors.NEUTRAL_2,
    textAlign: 'center',
    lineHeight: LINE_HEIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProgressBarProps = {
  nodes: readonly ProgressBarNodeType[];
  maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
  iconSrc?: string;
  iconAlt?: string;
};

type ProgressBarNodeProps = {
  node: ProgressBarNodeType;
  width: number;
  maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
  iconSrc?: string;
  iconAlt?: string;
};

export type ProgressBarNodeType = {
  id: string;
  label: string;
  completed: boolean;
  variation?: 'linear' | 'hot-spots';
};
